import { useAuthStore } from '@/stores/auth.js';

/**
 * Middleware доступа к разделу склада по настройке складского модуля.
 *
 * Раздел, включаемый настройкой, должен открываться только на том складе, где настройка
 * включена: адрес легко получить из истории браузера или закладки, и на «чужом» складе
 * пользователь увидел бы рабочий с виду экран, на котором ни одно действие не выполнимо.
 *
 * Настройки берутся из карточки пользователя (`useAuthStore().getModuleSettings`). При заходе
 * по прямому адресу или после обновления страницы карточка может быть ещё не загружена —
 * тогда сначала дожидаемся её, иначе проверка выкинула бы пользователя и со «своего» склада.
 *
 * Сообщение об отказе передаётся на страницу склада параметром адреса `accessError`:
 * тост живёт на корневом компоненте приложения, до которого из middleware не дотянуться,
 * поэтому его показывает страница-получатель (`pages/storehouse/Index.vue`).
 *
 * @param {string} settingKey - ключ настройки модуля, включающей раздел
 * @param {Object} options - дополнительные опции
 * @param {string} options.message - текст отказа для пользователя
 * @returns {Function} middleware функция
 */
export default function moduleSettingGuard(settingKey, options = {}) {
  const {
    message = 'Раздел не подключён на этом складе'
  } = options;

  return async function (to, from, next) {
    const moduleId = to.params.id || to.params.moduleId;

    if (!moduleId) {
      return next({ path: '/', query: { accessError: 'Склад не указан' } });
    }

    const authStore = useAuthStore();

    // Карточка пользователя ещё не загружена — ждём её, а не отказываем сразу
    if (!authStore.user?.modules) {
      try {
        await authStore.attempt_user();
      } catch (error) {
        console.error('Не удалось загрузить данные пользователя для проверки доступа:', error);

        return next({ path: '/', query: { accessError: message } });
      }
    }

    const settings = authStore.getModuleSettings(moduleId);

    if (settings?.[settingKey] === true) {
      return next();
    }

    return next({ path: `/storehouse/${moduleId}`, query: { accessError: message } });
  };
}
